// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * PersonalDataCard - Dashboard-kaart voor persoonlijke gegevens.
 * Toont en bewerkt naam, geboortedatum, adres, telefoon, e-mail inline.
 * Gebruikt EXACT DEZELFDE opslag als Step01PersonalData (wizard stap 0):
 *   db.wishes, category PERSONAL, geen moduleKey-filter (zelfde als wizard).
 * loadOwnerDob() blijft werken via encryptedProfile fallback.
 */
import { useState, useEffect } from 'react'
import { User, Pencil, Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import { Input }           from '@/components/ui'
import { useAuthStore }    from '@/store'
import { AddressFields }   from '@/components/ui/AddressFields'
import { getMasterKey, getFirstProfile, saveModuleProgress } from '@/services/profile'
import { db }              from '@/lib/db'
import { generateUUID, nowISO } from '@/lib/utils'
import { WishCategory, CompletionStatus, SyncStatus } from '@/types'

interface PersonalData {
  firstName: string; lastName: string; dateOfBirth: string
  email: string; phone: string
  street: string; houseNumber: string; houseNumberAddition: string
  postalCode: string; city: string
}
const EMPTY: PersonalData = {
  firstName: '', lastName: '', dateOfBirth: '',
  email: '', phone: '',
  street: '', houseNumber: '', houseNumberAddition: '',
  postalCode: '', city: '',
}

// ── Identical load logic as Step01PersonalData ────────────────────────────────
async function loadWizardPersonal(userId: string): Promise<PersonalData | null> {
  try {
    const rec = await db.wishes
      .where('[userId+category]')
      .equals([userId, WishCategory.PERSONAL])
      .first()
    if (!rec?.encryptedContent || !rec.blobIv) return null
    const key = getMasterKey(userId)
    if (!key) return null
    const iv  = Uint8Array.from(atob(rec.blobIv), c => c.charCodeAt(0))
    const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, rec.encryptedContent)
    return JSON.parse(new TextDecoder().decode(dec)) as PersonalData
  } catch { return null }
}

async function loadFromProfile(userId: string): Promise<Partial<PersonalData> | null> {
  try {
    const profile = await getFirstProfile()
    if (!profile) return null
    const key = getMasterKey(userId)
    if (!key || !profile.encryptedProfile || !profile.blobIv)
      return { firstName: profile.firstName }
    const iv  = Uint8Array.from(atob(profile.blobIv), c => c.charCodeAt(0))
    const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, profile.encryptedProfile)
    const d   = JSON.parse(new TextDecoder().decode(dec))
    let street = d.street ?? '', houseNumber = d.houseNumber ?? '',
        houseNumberAddition = d.houseNumberAddition ?? '',
        postalCode = '', city = ''
    const raw = (d.address ?? '').trim()
    if (street || houseNumber) {
      const m = raw.match(/\b(\d{4}\s?[A-Z]{2})\s+(.+)$/)
      if (m) { postalCode = m[1]; city = m[2] }
    } else {
      const parts = raw.split(/\s+/)
      if (/^\d{4}[A-Z]{2}$/.test(parts[0])) {
        postalCode = parts[0]; city = parts.slice(1).join(' ')
      } else if (/^\d{4}$/.test(parts[0]) && /^[A-Z]{2}$/.test(parts[1])) {
        postalCode = `${parts[0]} ${parts[1]}`; city = parts.slice(2).join(' ')
      } else { city = raw }
    }
    return {
      firstName: profile.firstName ?? '', lastName: d.lastName ?? '',
      dateOfBirth: d.dateOfBirth ?? '', email: d.email ?? '', phone: d.phone ?? '',
      street, houseNumber, houseNumberAddition, postalCode, city,
    }
  } catch { return null }
}

// ── Identical persist logic as Step01PersonalData ─────────────────────────────
async function persistPersonal(userId: string, data: PersonalData) {
  const enc = new TextEncoder().encode(JSON.stringify(data)).buffer
  const key = getMasterKey(userId)
  let blob: ArrayBuffer = enc, iv = ''
  if (key) {
    const ivArr = crypto.getRandomValues(new Uint8Array(12))
    blob = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivArr }, key, enc)
    iv = btoa(String.fromCharCode(...ivArr))
  }
  const coreFields = ['firstName','lastName','dateOfBirth','email','phone','postalCode','city'] as const
  const filled = coreFields.filter(k => data[k].trim()).length
  const status = filled >= 3 ? CompletionStatus.IN_PROGRESS : CompletionStatus.NOT_STARTED
  const existing = await db.wishes
    .where('[userId+category]').equals([userId, WishCategory.PERSONAL]).first()
  if (existing) {
    await db.wishes.update(existing.id, {
      encryptedContent: blob, blobIv: iv,
      completionStatus: status, lastUpdatedAt: nowISO(), syncStatus: SyncStatus.PENDING,
    })
  } else {
    await db.wishes.add({
      id: generateUUID(), userId, category: WishCategory.PERSONAL, sortOrder: 0,
      completionStatus: status, lastUpdatedAt: nowISO(), createdAt: nowISO(),
      syncStatus: SyncStatus.PENDING, cloudSyncId: null,
      encryptedContent: blob, blobIv: iv,
    })
  }
  await saveModuleProgress(userId, 'intake', filled, 7)
}

// ── Component ─────────────────────────────────────────────────────────────────
export function PersonalDataCard() {
  const { userId }            = useAuthStore()
  const [data,    setData]    = useState<PersonalData>(EMPTY)
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [draft,   setDraft]   = useState<PersonalData>(EMPTY)
  const [saving,  setSaving]  = useState(false)

  useEffect(() => {
    if (!userId) return
    loadWizardPersonal(userId).then(async d => {
      if (d) {
        // Vul lege velden aan vanuit profiel
        const prof = await loadFromProfile(userId)
        const merged = { ...d }
        if (prof) {
          const keys: (keyof PersonalData)[] = ['firstName','lastName','dateOfBirth','email','phone','postalCode','city']
          for (const k of keys) if (!merged[k] && prof[k]) merged[k] = prof[k] as string
        }
        setData(merged)
      } else {
        const prof = await loadFromProfile(userId)
        if (prof) setData(prev => ({ ...prev, ...prof }))
      }
    })
  }, [userId])

  function startEdit() {
    setDraft({ ...data })
    setEditing(true)
    setExpanded(true)
  }

  async function confirmEdit() {
    if (!userId) return
    setSaving(true)
    setData(draft)
    await persistPersonal(userId, draft)
    setSaving(false)
    setEditing(false)
  }

  function cancelEdit() {
    setDraft({ ...data })
    setEditing(false)
  }

  const upd = (k: keyof PersonalData, v: string) =>
    setDraft(p => ({ ...p, [k]: v }))

  const d = editing ? draft : data
  const hasData = !!(data.firstName || data.email)
  const displayName = [data.firstName, data.lastName].filter(Boolean).join(' ') || '-'
  const displayAdres = [
    [data.street, data.houseNumber, data.houseNumberAddition].filter(Boolean).join(' '),
    [data.postalCode, data.city].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ')

  return (
    <div className="rounded-2xl border border-sand-300 bg-white overflow-hidden">

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sage-50 text-sage-700 flex-shrink-0">
          <User className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink">Mijn gegevens</p>
          {!expanded && (
            <p className="text-xs text-ink-4 truncate mt-0.5">
              {hasData ? displayName : 'Naam, adres en contactgegevens'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!editing ? (
            <>
              <button
                onClick={startEdit}
                title="Bewerken"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-sage-50 hover:text-sage-700 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setExpanded(o => !o)}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-sand-100 transition-colors"
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={confirmEdit}
                disabled={saving}
                title="Opslaan"
                className="flex h-7 w-7 items-center justify-center rounded-lg bg-sage-50 text-sage-700 hover:bg-sage-100 transition-colors disabled:opacity-50"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={cancelEdit}
                disabled={saving}
                title="Annuleren"
                className="flex h-7 w-7 items-center justify-center rounded-lg text-ink-4 hover:bg-status-alertBg hover:text-status-alertText transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-sand-100">
          {!editing ? (
            <div className="flex flex-col gap-0 pt-3">
              {([
                { label: 'Naam',
                  value: displayName },
                { label: 'Geboortedatum',
                  value: data.dateOfBirth
                    ? new Date(data.dateOfBirth).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
                    : '' },
                { label: 'E-mail',   value: data.email },
                { label: 'Telefoon', value: data.phone },
                { label: 'Adres',    value: displayAdres },
              ] as const).map(({ label, value }) => value && value !== '-' ? (
                <div key={label} className="flex items-start justify-between gap-4 py-2 border-b border-sand-100 last:border-0">
                  <span className="text-xs text-ink-4 flex-shrink-0 w-28">{label}</span>
                  <span className="text-xs text-ink font-medium text-right flex-1">{value}</span>
                </div>
              ) : null)}
              {!hasData && (
                <p className="text-xs text-ink-4 py-3 text-center italic">
                  Nog niet ingevuld - klik op het potlood om te beginnen.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 pt-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input label="Voornaam"   value={d.firstName}  onChange={e => upd('firstName', e.target.value)}  placeholder="Marianne" autoFocus />
                <Input label="Achternaam" value={d.lastName}   onChange={e => upd('lastName',  e.target.value)}  placeholder="de Vries" />
              </div>
              <Input label="Geboortedatum" type="date" value={d.dateOfBirth}
                onChange={e => upd('dateOfBirth', e.target.value)} />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input label="E-mailadres"    type="email" value={d.email} onChange={e => upd('email', e.target.value)} placeholder="naam@voorbeeld.nl" />
                <Input label="Telefoonnummer" type="tel"   value={d.phone} onChange={e => upd('phone', e.target.value)} placeholder="06 12 34 56 78" />
              </div>
              <AddressFields
                values={{
                  postalCode:          d.postalCode,
                  houseNumber:         d.houseNumber,
                  houseNumberAddition: d.houseNumberAddition,
                  street:              d.street,
                  city:                d.city,
                }}
                onChange={(field, value) => upd(field as keyof PersonalData, value)}
                showAddition
              />
              <button
                type="button"
                onClick={confirmEdit}
                disabled={saving}
                className="flex items-center justify-center gap-2 w-full rounded-xl bg-moss text-white text-sm font-semibold py-3 hover:bg-moss-light transition-colors disabled:opacity-50"
              >
                <Check className="h-4 w-4" />
                {saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
